import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Interface for translation optimization configuration
 */
interface OptimizationConfig {
    enableTreeShaking: boolean;
    enableCompression: boolean;
    enableMinification: boolean;
    removeUnusedKeys: boolean;
    optimizeForProduction: boolean;
    bundleAnalysis: boolean;
}

/**
 * Interface for optimization results
 */
interface OptimizationResult {
    originalSize: number;
    optimizedSize: number;
    compressionRatio: number;
    removedKeys: string[];
    optimizationTime: number;
}

/**
 * Service for optimizing translation files for production builds
 */
@Injectable({
    providedIn: 'root',
})
export class TranslationBuildOptimizerService {
    private config: OptimizationConfig;
    private usedKeys = new Set<string>();
    private keyUsageStats = new Map<string, number>();

    constructor() {
        this.config = {
            enableTreeShaking: true,
            enableCompression: true,
            enableMinification: true,
            removeUnusedKeys: true,
            optimizeForProduction: true,
            bundleAnalysis: false,
        };
    }

    /**
     * Track usage of translation keys
     */
    trackKeyUsage(key: string): void {
        this.usedKeys.add(key);
        const currentCount = this.keyUsageStats.get(key) || 0;
        this.keyUsageStats.set(key, currentCount + 1);
    }

    /**
     * Get usage statistics for translation keys
     */
    getKeyUsageStats(): Map<string, number> {
        return new Map(this.keyUsageStats);
    }

    /**
     * Get list of used translation keys
     */
    getUsedKeys(): string[] {
        return Array.from(this.usedKeys);
    }

    /**
     * Optimize translation object by removing unused keys
     */
    optimizeTranslationObject(
        translations: any,
        usedKeys?: string[]
    ): Observable<OptimizationResult> {
        return new Observable((observer) => {
            const startTime = performance.now();
            const originalSize = this.calculateObjectSize(translations);

            try {
                let optimizedTranslations = translations;
                const removedKeys: string[] = [];

                if (this.config.removeUnusedKeys && usedKeys) {
                    const result = this.removeUnusedKeys(
                        translations,
                        usedKeys
                    );
                    optimizedTranslations = result.optimized;
                    removedKeys.push(...result.removed);
                }

                if (this.config.enableMinification) {
                    optimizedTranslations = this.minifyTranslations(
                        optimizedTranslations
                    );
                }

                const optimizedSize = this.calculateObjectSize(
                    optimizedTranslations
                );
                const compressionRatio =
                    originalSize > 0
                        ? (originalSize - optimizedSize) / originalSize
                        : 0;
                const optimizationTime = performance.now() - startTime;

                const result: OptimizationResult = {
                    originalSize,
                    optimizedSize,
                    compressionRatio,
                    removedKeys,
                    optimizationTime,
                };

                console.log('Translation optimization completed:', result);
                observer.next(result);
                observer.complete();
            } catch (error) {
                console.error('Translation optimization failed:', error);
                observer.error(error);
            }
        });
    }

    /**
     * Remove unused translation keys
     */
    private removeUnusedKeys(
        translations: any,
        usedKeys: string[]
    ): { optimized: any; removed: string[] } {
        const optimized = {};
        const removed: string[] = [];
        const usedKeySet = new Set(usedKeys);

        this.processTranslationObject(
            translations,
            optimized,
            '',
            usedKeySet,
            removed
        );

        console.log(`Removed ${removed.length} unused translation keys`);
        return { optimized, removed };
    }

    /**
     * Recursively process translation object to remove unused keys
     */
    private processTranslationObject(
        source: any,
        target: any,
        currentPath: string,
        usedKeys: Set<string>,
        removed: string[]
    ): void {
        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                const fullPath = currentPath ? `${currentPath}.${key}` : key;

                if (typeof source[key] === 'object' && source[key] !== null) {
                    // Recursively process nested objects
                    target[key] = {};
                    this.processTranslationObject(
                        source[key],
                        target[key],
                        fullPath,
                        usedKeys,
                        removed
                    );

                    // Remove empty objects
                    if (Object.keys(target[key]).length === 0) {
                        delete target[key];
                        removed.push(fullPath);
                    }
                } else {
                    // Check if this key is used
                    if (usedKeys.has(fullPath)) {
                        target[key] = source[key];
                    } else {
                        removed.push(fullPath);
                    }
                }
            }
        }
    }

    /**
     * Minify translation object by removing unnecessary whitespace and formatting
     */
    private minifyTranslations(translations: any): any {
        // For JSON translations, minification is primarily about structure optimization
        // Remove any unnecessary properties or metadata
        return this.deepCleanObject(translations);
    }

    /**
     * Deep clean object by removing null, undefined, and empty values
     */
    private deepCleanObject(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj
                .map((item) => this.deepCleanObject(item))
                .filter((item) => item !== null && item !== undefined);
        }

        const cleaned: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const value = this.deepCleanObject(obj[key]);
                if (value !== null && value !== undefined && value !== '') {
                    cleaned[key] = value;
                }
            }
        }

        return cleaned;
    }

    /**
     * Calculate approximate size of an object in bytes
     */
    private calculateObjectSize(obj: any): number {
        const jsonString = JSON.stringify(obj);
        return new Blob([jsonString]).size;
    }

    /**
     * Analyze translation bundle for optimization opportunities
     */
    analyzeBundleOptimization(translations: {
        [lang: string]: any;
    }): Observable<{
        totalSize: number;
        languageSizes: { [lang: string]: number };
        duplicateKeys: string[];
        unusedKeys: string[];
        optimizationPotential: number;
    }> {
        return new Observable((observer) => {
            try {
                let totalSize = 0;
                const languageSizes: { [lang: string]: number } = {};
                const allKeys = new Set<string>();
                const keysByLanguage: { [lang: string]: Set<string> } = {};

                // Analyze each language
                for (const lang in translations) {
                    const size = this.calculateObjectSize(translations[lang]);
                    languageSizes[lang] = size;
                    totalSize += size;

                    // Collect keys for duplicate analysis
                    const keys = this.extractAllKeys(translations[lang]);
                    keysByLanguage[lang] = new Set(keys);
                    keys.forEach((key) => allKeys.add(key));
                }

                // Find duplicate keys across languages
                const duplicateKeys = this.findDuplicateKeys(keysByLanguage);

                // Find unused keys
                const unusedKeys = this.findUnusedKeys(Array.from(allKeys));

                // Calculate optimization potential
                const optimizationPotential =
                    this.calculateOptimizationPotential(
                        totalSize,
                        duplicateKeys.length,
                        unusedKeys.length
                    );

                const analysis = {
                    totalSize,
                    languageSizes,
                    duplicateKeys,
                    unusedKeys,
                    optimizationPotential,
                };

                console.log('Bundle analysis completed:', analysis);
                observer.next(analysis);
                observer.complete();
            } catch (error) {
                console.error('Bundle analysis failed:', error);
                observer.error(error);
            }
        });
    }

    /**
     * Extract all keys from a translation object
     */
    private extractAllKeys(obj: any, prefix = ''): string[] {
        const keys: string[] = [];

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const fullKey = prefix ? `${prefix}.${key}` : key;

                if (typeof obj[key] === 'object' && obj[key] !== null) {
                    keys.push(...this.extractAllKeys(obj[key], fullKey));
                } else {
                    keys.push(fullKey);
                }
            }
        }

        return keys;
    }

    /**
     * Find duplicate keys across languages
     */
    private findDuplicateKeys(keysByLanguage: {
        [lang: string]: Set<string>;
    }): string[] {
        const languages = Object.keys(keysByLanguage);
        const duplicates = new Set<string>();

        if (languages.length < 2) {
            return [];
        }

        const firstLangKeys = keysByLanguage[languages[0]];

        for (const key of firstLangKeys) {
            let isDuplicate = true;
            for (let i = 1; i < languages.length; i++) {
                if (!keysByLanguage[languages[i]].has(key)) {
                    isDuplicate = false;
                    break;
                }
            }
            if (isDuplicate) {
                duplicates.add(key);
            }
        }

        return Array.from(duplicates);
    }

    /**
     * Find unused keys based on tracked usage
     */
    private findUnusedKeys(allKeys: string[]): string[] {
        return allKeys.filter((key) => !this.usedKeys.has(key));
    }

    /**
     * Calculate optimization potential as a percentage
     */
    private calculateOptimizationPotential(
        totalSize: number,
        duplicateCount: number,
        unusedCount: number
    ): number {
        // Rough estimation: each unused key saves ~50 bytes, duplicates save ~30 bytes
        const estimatedSavings = unusedCount * 50 + duplicateCount * 30;
        return totalSize > 0
            ? Math.min((estimatedSavings / totalSize) * 100, 100)
            : 0;
    }

    /**
     * Generate optimization report
     */
    generateOptimizationReport(): Observable<{
        keyUsage: { key: string; count: number }[];
        recommendations: string[];
        potentialSavings: number;
        performanceImpact: string;
    }> {
        return new Observable((observer) => {
            try {
                // Sort keys by usage frequency
                const keyUsage = Array.from(this.keyUsageStats.entries())
                    .map(([key, count]) => ({ key, count }))
                    .sort((a, b) => b.count - a.count);

                const recommendations: string[] = [];
                let potentialSavings = 0;

                // Generate recommendations based on usage patterns
                const unusedKeys = keyUsage.filter((item) => item.count === 0);
                if (unusedKeys.length > 0) {
                    recommendations.push(
                        `Remove ${unusedKeys.length} unused translation keys`
                    );
                    potentialSavings += unusedKeys.length * 50; // Estimated 50 bytes per key
                }

                const rarelyUsedKeys = keyUsage.filter(
                    (item) => item.count > 0 && item.count < 5
                );
                if (rarelyUsedKeys.length > 0) {
                    recommendations.push(
                        `Consider lazy loading for ${rarelyUsedKeys.length} rarely used keys`
                    );
                }

                if (this.config.enableCompression) {
                    recommendations.push(
                        'Enable gzip compression for translation files'
                    );
                    potentialSavings *= 0.7; // Gzip typically achieves ~30% compression
                }

                if (keyUsage.length === 0) {
                    recommendations.push(
                        'Start tracking key usage to identify optimization opportunities'
                    );
                }

                const performanceImpact =
                    this.assessPerformanceImpact(potentialSavings);

                const report = {
                    keyUsage,
                    recommendations,
                    potentialSavings: Math.round(potentialSavings),
                    performanceImpact,
                };

                console.log('Optimization report generated:', report);
                observer.next(report);
                observer.complete();
            } catch (error) {
                console.error('Failed to generate optimization report:', error);
                observer.error(error);
            }
        });
    }

    /**
     * Assess performance impact of optimizations
     */
    private assessPerformanceImpact(savings: number): string {
        if (savings > 10000)
            return 'High - Significant load time improvement expected';
        if (savings > 5000)
            return 'Medium - Noticeable performance improvement';
        if (savings > 1000) return 'Low - Minor performance improvement';
        return 'Minimal - Little to no noticeable impact';
    }

    /**
     * Update optimization configuration
     */
    updateConfig(newConfig: Partial<OptimizationConfig>): void {
        this.config = { ...this.config, ...newConfig };
        console.log('Optimization configuration updated:', this.config);
    }

    /**
     * Get current configuration
     */
    getConfig(): OptimizationConfig {
        return { ...this.config };
    }

    /**
     * Reset tracking data
     */
    resetTracking(): void {
        this.usedKeys.clear();
        this.keyUsageStats.clear();
        console.log('Translation key tracking data reset');
    }

    /**
     * Export tracking data for analysis
     */
    exportTrackingData(): {
        usedKeys: string[];
        keyUsageStats: { [key: string]: number };
        totalTrackedKeys: number;
        timestamp: number;
    } {
        return {
            usedKeys: Array.from(this.usedKeys),
            keyUsageStats: Object.fromEntries(this.keyUsageStats),
            totalTrackedKeys: this.usedKeys.size,
            timestamp: Date.now(),
        };
    }
}
